import { QueryParam } from '../utils/query-param'
import { DataSurfaceViewMode, TPagination } from './types'

export interface IViewDataQueryParams {
  mode?: DataSurfaceViewMode
}

export function setViewModeToURL(mode?: DataSurfaceViewMode, defaultMode?: DataSurfaceViewMode) {
  // If window is undefined (e.g., during SSR), do nothing
  if (typeof window === 'undefined') return
  try {
    const updateParams: IViewDataQueryParams = { mode: mode === defaultMode ? undefined : mode }

    // Update URL with view mode
    if (updateParams.mode) {
      QueryParam.replacePatch<IViewDataQueryParams>(updateParams)
    } else {
      // If no view mode to update, remove existing mode from URL
      QueryParam.replaceDeletes<IViewDataQueryParams>('mode')
    }
  } catch (error) {
    console.error('Error syncing view mode to URL:', error)
  }
}

export function getViewModeFromURL(defaultMode?: DataSurfaceViewMode): DataSurfaceViewMode | undefined {
  // If window is undefined (e.g., during SSR), return defaultViewMode
  if (typeof window === 'undefined') return defaultMode
  try {
    const q = QueryParam.gets<IViewDataQueryParams>('mode')
    return q.mode ? (q.mode as DataSurfaceViewMode) : defaultMode
  } catch (error) {
    console.error('Error getting view mode from URL:', error)
    return defaultMode
  }
}

export interface IPaginationQueryParams {
  page?: string
  pageSize?: string
}

/**
 * @en Synchronize pagination to URL query parameters. Each field is handled independently:
 *     if it differs from default → add to URL; if same or undefined → remove from URL.
 * @vi Dong bo pagination vao URL query parameters. Tung field duoc xu ly doc lap:
 *     neu khac default → them vao URL; neu bang hoac undefined → xoa khoi URL.
 *
 * @param pagination - Current pagination to sync
 * @param defaultPagination - Default pagination values used as baseline
 */
export function setPaginationToURL(pagination?: TPagination, defaultPagination?: TPagination) {
  if (typeof window === 'undefined') return

  try {
    const patchParams: IPaginationQueryParams = {}
    const deleteKeys: (keyof IPaginationQueryParams)[] = []

    // page: differs from default → add to URL; same or missing → remove
    if (pagination?.page !== undefined && pagination.page !== defaultPagination?.page) {
      patchParams.page = String(pagination.page)
    } else {
      deleteKeys.push('page')
    }

    // pageSize: differs from default → add to URL; same or missing → remove
    if (pagination?.pageSize !== undefined && pagination.pageSize !== defaultPagination?.pageSize) {
      patchParams.pageSize = String(pagination.pageSize)
    } else {
      deleteKeys.push('pageSize')
    }

    if (Object.keys(patchParams).length > 0) QueryParam.replacePatch<IPaginationQueryParams>(patchParams)
    if (deleteKeys.length > 0) QueryParam.replaceDeletes<IPaginationQueryParams>(...deleteKeys)
  } catch (error) {
    console.error('Error syncing pagination to URL:', error)
  }
}

/**
 * @en Retrieve pagination from URL query parameters.
 * @vi Lay pagination tu URL query parameters.
 *
 * @param defaultPagination - Default pagination to return if URL has no pagination
 * @returns Pagination from URL or defaultPagination
 */
export function getPaginationFromURL(defaultPagination?: TPagination): TPagination | undefined {
  // If window is undefined (e.g., during SSR), return defaultPagination
  if (typeof window === 'undefined') return defaultPagination

  try {
    const q = QueryParam.gets<IPaginationQueryParams>('page', 'pageSize')

    // Parse values from URL
    const page = q.page ? parseInt(q.page, 10) : undefined
    const pageSize = q.pageSize ? parseInt(q.pageSize, 10) : undefined

    // If no pagination in URL, return default
    if (page === undefined && pageSize === undefined) {
      return defaultPagination
    }

    // Merge with default pagination
    return {
      ...defaultPagination,
      page: page !== undefined && !isNaN(page) ? page : defaultPagination?.page,
      pageSize: pageSize !== undefined && !isNaN(pageSize) ? pageSize : defaultPagination?.pageSize
    }
  } catch (error) {
    console.error('Error getting pagination from URL:', error)
    return defaultPagination
  }
}
